<template>
  <div class="page-stack">
    <div class="sticky-toolbar">
      <a-card :bordered="false" class="sticky-toolbar-card">
        <a-space wrap align="center">
          <div style="font-weight:600">Дата:</div>
          <a-button @click="shiftDate(-1)"><LeftOutlined /></a-button>
          <a-date-picker v-model:value="dateValue" format="YYYY-MM-DD" :allowClear="false" />
          <a-button @click="shiftDate(1)"><RightOutlined /></a-button>
          <a-button @click="load" :loading="loading">Обновить</a-button>
        </a-space>
      </a-card>
    </div>

    <a-card :bordered="false">
      <a-collapse :bordered="false">
        <a-collapse-panel key="week-summary" header="Неделя">
          <WeekOrdersCard :items="weekItems" :selectedDate="dateStr()" />
        </a-collapse-panel>
      </a-collapse>
    </a-card>

    <a-card title="Количество порций" :bordered="false">
      <a-row :gutter="[12,12]">
        <a-col :xs="24" :sm="12" :md="6" v-for="m in visibleMealCards" :key="m.meal">
          <a-card size="small">
            <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px">
              <div style="font-weight:600">{{ m.title }}</div>
            </div>

            <a-input-number
              style="width:100%"
              :min="0"
              v-model:value="localMeals[m.meal]"
              :disabled="isMealLocked(m.meal)"
            />


            <div v-if="isMealLocked(m.meal)" style="margin-top:8px;color:#ccc">
              Заблокировано по дедлайну.
            </div>
          </a-card>
        </a-col>
      </a-row>
    </a-card>

    <a-card title="Комментарий" :bordered="false">
      <a-textarea
        v-model:value="comment"
        :rows="4"
        placeholder="Без хлеба и напитков..."
        :disabled="commentLocked"
      />
      <div v-if="commentLocked" style="margin-top:8px;color:#ccc">
        Комментарий заблокирован после дедлайна.
      </div>
    </a-card>

    <a-card title="В том числе особые количества" :bordered="false">
      <a-table
        :dataSource="specialTypes"
        :pagination="false"
        rowKey="id"
        size="small"
        class="special-qty-table"
      >
        <a-table-column key="name" title="Особое" dataIndex="name" />

        <a-table-column
          v-for="meal in visibleMealCards"
          :key="meal.meal"
          :title="meal.title"
        >
          <template #default="{ record }">
            <a-space direction="vertical" size="small" class="special-cell">
              <a-input-number
                :min="0"
                v-model:value="localSpecials[meal.meal][record.id]"
                class="special-qty-input"
                :disabled="isMealLocked(meal.meal)"
              />
            </a-space>
          </template>
        </a-table-column>
      </a-table>
    </a-card>

    <a-modal v-model:open="addChildOpen" title="Добавить ребёнка" @ok="saveAddChild">
      <a-form layout="vertical">
        <a-form-item label="Приёмы пищи">
          <a-checkbox-group v-model:value="addChildMeals">
            <a-space direction="vertical">
              <a-checkbox
                v-for="meal in visibleMealCards"
                :key="meal.meal"
                :value="meal.meal"
                :disabled="isMealLocked(meal.meal)"
              >
                {{ meal.title }}
              </a-checkbox>
            </a-space>
          </a-checkbox-group>
        </a-form-item>

        <a-form-item label="Ребёнок">
          <a-select
            v-model:value="addChildId"
            :options="activeChildOptions"
            style="width:100%"
          />
        </a-form-item>

        <a-form-item label="Примечание">
          <a-textarea v-model:value="addChildNote" :rows="3" />
        </a-form-item>
      </a-form>
    </a-modal>

    <a-card title="Дети с особенностями (в заказе)" :bordered="false">
      <a-space style="margin-bottom:12px">
        <a-button type="primary" @click="openAddChildMulti" :disabled="allMealsLocked">
          Добавить ребёнка
        </a-button>
      </a-space>

      <a-tabs>
        <a-tab-pane v-for="m in visibleMealCards" :key="m.meal" :tab="m.title">
          <a-button @click="openAddChild(m.meal)" :disabled="isMealLocked(m.meal)">Добавить ребёнка {{ label(m.meal) }}</a-button>
          <div v-if="isMealLocked(m.meal)" style="margin-top:8px;color:#b00">
            Изменения по этому приёму пищи доступны только кухне.
          </div>
          <br />
          <br />
          <a-table
            :dataSource="childrenByMeal[m.meal]"
            :pagination="false"
            rowKey="child_id"
            size="small"
          >
            <a-table-column key="full_name" title="Ребёнок" dataIndex="full_name" />
            <a-table-column key="tags" title="Теги">
              <template #default="{ record }">
                <ChildTagIcons :tags="record.tags" />
              </template>
            </a-table-column>
            <a-table-column key="child_comment" title="Комментарий" dataIndex="child_comment" />
            <a-table-column key="order_note" title="Примечание к заказу" dataIndex="order_note" />
            <a-table-column key="actions" title="" width="140">
              <template #default="{ record }">
                <a-button
                  danger
                  size="small"
                  @click="deleteChild(m.meal, record.child_id)"
                  :disabled="isMealLocked(m.meal)"
                >
                  Удалить
                </a-button>
              </template>
            </a-table-column>
          </a-table>
        </a-tab-pane>
      </a-tabs>
    </a-card>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onBeforeUnmount } from 'vue'
import dayjs from 'dayjs'
import { message } from 'ant-design-vue'
import { LeftOutlined, RightOutlined } from '@ant-design/icons-vue'
import { http } from '../../api/http'
import { getChildren } from '../../api/children'
import { getSpecialTypes } from '../../api/specialTypes'
import { getOrderByDate, addChildToMeal, removeChildFromMeal, setOrderSpecialQty, getWeekOrders } from '../../api/orders'
import { getMe } from '../../api/profile'
import WeekOrdersCard from '../../components/WeekOrdersCard.vue'
import ChildTagIcons from '../../components/ChildTagIcons.vue'
import { label } from '../../utils/labels'
import { isMealLockedByCutoff, isOrderCommentLocked } from '../../utils/orderCutoffs'

const loading = ref(false)

const dateValue = ref(dayjs())

const data = ref(null)
const comment = ref('')
const localMeals = reactive({ BREAKFAST: 0, LUNCH: 0, SNACK: 0, DINNER: 0 })
const loadedMeals = reactive({ BREAKFAST: 0, LUNCH: 0, SNACK: 0, DINNER: 0 })
const loadedComment = ref('')

const me = ref({})

const allChildren = ref([])
const specialTypes = ref([])
const weekItems = ref([])
const suppressAutosave = ref(true)
const autosaveUnlockTimer = ref(null)
const mealSaveTimers = {}
const specialSaveTimers = {}
const commentSaveTimer = ref(null)

const addChildOpen = ref(false)
const addChildMeals = ref([])
const addChildId = ref(null)
const addChildNote = ref('')

const locks = reactive({ BREAKFAST: false, LUNCH: false, SNACK: false, DINNER: false })

const mealCards = [
  { meal: 'BREAKFAST', title: 'Завтрак' },
  { meal: 'LUNCH', title: 'Обед' },
  { meal: 'SNACK', title: 'Полдник' },
  { meal: 'DINNER', title: 'Ужин' },
]

const childrenByMeal = computed(() => data.value?.children || { BREAKFAST: [], LUNCH: [], SNACK: [], DINNER: [] })
const mealVisibility = computed(() => me.value?.meal_visibility || {})
const visibleMealCards = computed(() =>
  mealCards.filter(item => mealVisibility.value[item.meal] !== false)
)
const allMealsLocked = computed(() => visibleMealCards.value.every(item => isMealLocked(item.meal)))
const activeChildOptions = computed(() =>
  allChildren.value
    .filter(child => !!child.active)
    .map(child => ({ value: child.id, label: child.full_name }))
)

const localSpecials = ref({
  BREAKFAST: {},
  LUNCH: {},
  SNACK: {},
  DINNER: {},
})
const loadedSpecials = ref({
  BREAKFAST: {},
  LUNCH: {},
  SNACK: {},
  DINNER: {},
})

const commentLocked = computed(() => isOrderCommentLocked(dateValue.value))

function dateStr() {
  return dateValue.value.format('YYYY-MM-DD')
}

function shiftDate(days) {
  dateValue.value = dayjs(dateValue.value).add(days, 'day')
}

function isMealLocked(meal) {
  return locks[meal] || isMealLockedByCutoff(dateValue.value, meal)
}

function cloneSpecialsMap(source) {
  return {
    BREAKFAST: { ...(source.BREAKFAST || {}) },
    LUNCH: { ...(source.LUNCH || {}) },
    SNACK: { ...(source.SNACK || {}) },
    DINNER: { ...(source.DINNER || {}) },
  }
}

function clearAutosaveTimers() {
  Object.values(mealSaveTimers).forEach(timer => clearTimeout(timer))
  Object.values(specialSaveTimers).forEach(timer => clearTimeout(timer))

  for (const key of Object.keys(mealSaveTimers)) {
    delete mealSaveTimers[key]
  }

  for (const key of Object.keys(specialSaveTimers)) {
    delete specialSaveTimers[key]
  }

  if (commentSaveTimer.value) {
    clearTimeout(commentSaveTimer.value)
    commentSaveTimer.value = null
  }
}

async function load() {
  loading.value = true
  suppressAutosave.value = true
  clearAutosaveTimers()
  try {
    const [meRes, orderRes, childrenRes, specialRes, weekRes] = await Promise.all([
      getMe(),
      getOrderByDate(dateStr()),
      getChildren(),
      getSpecialTypes(),
      getWeekOrders(dayjs(dateValue.value).startOf('week').format('YYYY-MM-DD')),
    ])

    data.value = orderRes.data.data
    comment.value = data.value.order.comment || ''
    loadedComment.value = comment.value
    specialTypes.value = specialRes.data.data || []

    for (const k of Object.keys(localMeals)) {
      localMeals[k] = data.value.meals[k] ?? 0
      loadedMeals[k] = localMeals[k]
      locks[k] = false
    }

    localSpecials.value = buildSpecialsMap(data.value.specials, specialTypes.value)
    loadedSpecials.value = cloneSpecialsMap(localSpecials.value)

    me.value = meRes.data.data || {}
    allChildren.value = childrenRes.data.data || []
    weekItems.value = weekRes.data.data || []
  } catch (e) {
    message.error(e?.response?.data?.error?.message || 'Не удалось загрузить заказ')
  } finally {
    loading.value = false
    if (autosaveUnlockTimer.value) {
      clearTimeout(autosaveUnlockTimer.value)
    }
    autosaveUnlockTimer.value = setTimeout(() => {
      suppressAutosave.value = false
    }, 0)
  }
}

async function saveMeal(meal) {
  if (localMeals[meal] === loadedMeals[meal]) {
    return
  }

  if (isMealLocked(meal)) {
    message.warning('Дедлайн прошёл. Позвоните повару.')
    return
  }

  try {
    await http.put(`/api/orders/${dateStr()}/meals/${meal}`, { qty: localMeals[meal] })
    locks[meal] = false
    loadedMeals[meal] = localMeals[meal]
  } catch (e) {
    const st = e?.response?.status
    if (st === 423) {
      locks[meal] = true
      message.warning('Дедлайн прошёл, позвоните повару')
    } else {
      message.error(e?.response?.data?.error?.message || 'Ошибка сохранения')
    }
  }
}

async function saveComment() {
  if (comment.value === loadedComment.value) {
    return
  }

  if (commentLocked.value) {
    message.warning('Комментарий после дедлайна может менять только кухня.')
    return
  }

  try {
    await http.put(`/api/orders/${dateStr()}/comment`, { comment: comment.value })
    loadedComment.value = comment.value
  } catch (e) {
    message.error(e?.response?.data?.error?.message || 'Ошибка сохранения')
  }
}

function openAddChild(meal) {
  if (isMealLocked(meal)) {
    message.warning('После дедлайна изменения вносит кухня.')
    return
  }

  addChildMeals.value = [meal]
  addChildId.value = null
  addChildNote.value = ''
  addChildOpen.value = true
}

function openAddChildMulti() {
  const availableMeals = visibleMealCards.value
    .map(item => item.meal)
    .filter(meal => !isMealLocked(meal))

  if (!availableMeals.length) {
    message.warning('После дедлайна изменения вносит кухня.')
    return
  }

  addChildMeals.value = [...availableMeals]
  addChildId.value = null
  addChildNote.value = ''
  addChildOpen.value = true
}

async function saveAddChild() {
  if (!addChildId.value) {
    message.warning('Выберите ребёнка')
    return
  }

  const selectedMeals = addChildMeals.value.filter(meal => !isMealLocked(meal))

  if (!selectedMeals.length) {
    message.warning('Выберите хотя бы один доступный приём пищи')
    return
  }

  try {
    for (const meal of selectedMeals) {
      await addChildToMeal(dateStr(), addChildId.value, meal, addChildNote.value)
    }
    addChildOpen.value = false
    message.success(selectedMeals.length === 1 ? 'Ребёнок добавлен' : 'Ребёнок добавлен в выбранные приёмы пищи')
    await load()
  } catch (e) {
    message.error(e?.response?.data?.error?.message || 'Ошибка добавления')
  }
}

async function deleteChild(meal, childId) {
  if (isMealLocked(meal)) {
    message.warning('После дедлайна изменения вносит кухня.')
    return
  }

  try {
    await removeChildFromMeal(dateStr(), childId, meal)
    message.success('Удалено')
    await load()
  } catch (e) {
    message.error(e?.response?.data?.error?.message || 'Ошибка удаления')
  }
}

async function saveSpecial(meal, specialTypeId) {
  const qty = Number(localSpecials.value[meal][specialTypeId] || 0)

  if (qty === Number(loadedSpecials.value[meal]?.[specialTypeId] || 0)) {
    return
  }

  if (isMealLocked(meal)) {
    message.warning('Дедлайн прошёл. Позвоните повару.')
    return
  }

  try {
    await setOrderSpecialQty(dateStr(), meal, specialTypeId, qty)
    loadedSpecials.value[meal][specialTypeId] = qty
  } catch (e) {
    message.error(e?.response?.data?.error?.message || 'Ошибка сохранения')
  }
}

function scheduleMealSave(meal) {
  if (mealSaveTimers[meal]) {
    clearTimeout(mealSaveTimers[meal])
  }

  mealSaveTimers[meal] = setTimeout(() => {
    delete mealSaveTimers[meal]
    saveMeal(meal)
  }, 500)
}

function scheduleCommentSave() {
  if (commentSaveTimer.value) {
    clearTimeout(commentSaveTimer.value)
  }

  commentSaveTimer.value = setTimeout(() => {
    commentSaveTimer.value = null
    saveComment()
  }, 700)
}

function scheduleSpecialSave(meal, specialTypeId) {
  const key = `${meal}:${specialTypeId}`

  if (specialSaveTimers[key]) {
    clearTimeout(specialSaveTimers[key])
  }

  specialSaveTimers[key] = setTimeout(() => {
    delete specialSaveTimers[key]
    saveSpecial(meal, specialTypeId)
  }, 500)
}

function buildSpecialsMap(orderSpecials, specialTypes) {
  const result = {
    BREAKFAST: {},
    LUNCH: {},
    SNACK: {},
    DINNER: {},
  }

  for (const meal of ['BREAKFAST', 'LUNCH', 'SNACK', 'DINNER']) {
    for (const st of specialTypes) {
      result[meal][st.id] = 0
    }
  }

  for (const meal of ['BREAKFAST', 'LUNCH', 'SNACK', 'DINNER']) {
    const items = orderSpecials?.[meal] || []
    for (const item of items) {
      result[meal][item.special_type_id] = item.qty
    }
  }

  return result
}

watch(dateValue, () => {
  load()
})

watch(
  () => visibleMealCards.value.map(item => localMeals[item.meal]),
  values => {
    if (suppressAutosave.value) {
      return
    }

    visibleMealCards.value.forEach((item, index) => {
      if (values[index] !== loadedMeals[item.meal]) {
        scheduleMealSave(item.meal)
      }
    })
  }
)

watch(comment, value => {
  if (suppressAutosave.value || value === loadedComment.value) {
    return
  }

  scheduleCommentSave()
})

watch(
  localSpecials,
  value => {
    if (suppressAutosave.value) {
      return
    }

    visibleMealCards.value.forEach(item => {
      Object.keys(value[item.meal] || {}).forEach(specialTypeId => {
        if (value[item.meal][specialTypeId] !== loadedSpecials.value[item.meal]?.[specialTypeId]) {
          scheduleSpecialSave(item.meal, specialTypeId)
        }
      })
    })
  },
  { deep: true }
)

onBeforeUnmount(() => {
  clearAutosaveTimers()

  if (autosaveUnlockTimer.value) {
    clearTimeout(autosaveUnlockTimer.value)
  }
})

load()
</script>

<style scoped>
.page-stack {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.sticky-toolbar {
  position: sticky;
  top: 16px;
  z-index: 20;
}

.sticky-toolbar-card :deep(.ant-card-body) {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.08);
}

.special-cell {
  width: 100%;
  min-width: 0;
}

.special-qty-input {
  width: 100%;
}

.special-qty-table :deep(.ant-table-cell) {
  vertical-align: top;
}

@media (max-width: 767px) {
  .sticky-toolbar {
    top: 8px;
  }

  .special-qty-table :deep(.ant-table-content) {
    overflow-x: visible;
  }

  .special-qty-table :deep(.ant-table-cell) {
    padding: 8px 6px;
  }

  .special-qty-input {
    width: 100%;
    min-width: 0;
  }

  .special-save-button {
    padding-inline: 8px;
    font-size: 12px;
  }
}
</style>
